"use client";

import { useState, useEffect, Dispatch, SetStateAction } from "react";

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

function getInitialValue<T>(key: string, initialState: T): T {
  if (typeof window === "undefined") return initialState;

  try {
    const savedValue = window.localStorage.getItem(key);
    if (!savedValue) return initialState;

    const parsed = JSON.parse(savedValue);

    if (parsed === null || parsed === undefined) return initialState;

    if (isPlainObject(initialState) && isPlainObject(parsed)) {
      return { ...(initialState as Record<string, unknown>), ...(parsed as Record<string, unknown>) } as unknown as T;
    }

    return parsed as T;
  } catch (error) {
    console.error(`Error reading localStorage key “${key}”:`, error);
    return initialState;
  }
}

export function useLocalStorageNew<T>(key: string, initialState: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => getInitialValue(key, initialState));

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(key, JSON.stringify(state));
    } catch (error) {
      console.error(`Error setting localStorage key “${key}”:`, error);
    }
  }, [key, state]);

  return [state, setState];
}
